// src/services/googleSheetsService.js
import axios from 'axios';

/**
 * Google Apps Script Web App URL
 * Replace this with your deployed script URL
 * IMPORTANT: Must end with /exec (not /dev)
 */
const GOOGLE_SCRIPT_URL = process.env.REACT_APP_MERCHANT_SIGNUP_WEBHOOK || 
  'https://script.google.com/macros/s/AKfycby_UaG9SJV7F0S71rusfFg0-vAkWJ1MrmS7zoRdJ20WkSG4_yk2A6zhnmP-1mYGezpOKQ/exec';

/**
 * Save merchant signup data to Google Sheets
 * @param {Object} data - Signup data
 * @param {string} data.merchantId - Merchant ID
 * @param {string} data.username - Username
 * @param {string} data.password - Password (consider security implications)
 * @param {string} data.parentClientId - Parent Client ID
 * @param {string} data.email - Email address
 * @param {string} data.phone - Phone number
 * @param {string} data.name - Full name
 * @param {string} data.brandName - Brand name
 * @returns {Promise<Object>} Response
 */
export const saveMerchantSignupToSheet = async (data) => {
  try {
    console.log('📊 Saving signup data to Google Sheets...');
    console.log('📤 Base URL:', GOOGLE_SCRIPT_URL);
    console.log('📤 Data:', { 
      merchantId: data.merchantId, 
      username: data.username,
      parentClientId: data.parentClientId,
      email: data.email,
      phone: data.phone,
      name: data.name,
      brandName: data.brandName
    });
    
    // Build query string for GET request
    const params = new URLSearchParams({
      merchantId: data.merchantId || '',
      username: data.username || '',
      password: data.password || '',
      parentClientId: data.parentClientId || '',
      email: data.email || '',
      phone: data.phone || '',
      name: data.name || '',
      brandName: data.brandName || '',
    });
    
    const fullUrl = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;
    console.log('📤 Full URL:', fullUrl);
    
    const response = await axios.get(fullUrl, {
      timeout: 15000, // 15 seconds (increased for Google Scripts)
      headers: {
        'Accept': 'application/json',
      },
      validateStatus: function (status) {
        // Accept any status code (we'll handle it)
        return status >= 200 && status < 500;
      },
    });
    
    console.log('✅ Google Sheets response:', response.data);
    
    if (response.data?.success === false) {
      throw new Error(response.data?.error || 'Failed to save to Google Sheets');
    }
    
    return { success: true, data: response.data };
    
  } catch (error) {
    console.error('❌ Failed to save to Google Sheets:', error);
    console.error('❌ Error details:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      url: GOOGLE_SCRIPT_URL
    });
    
    // Don't throw - this is non-critical, shouldn't stop onboarding
    return { 
      success: false, 
      error: error.message || 'Failed to save to Google Sheets' 
    };
  }
};

/**
 * Google Apps Script Web App URL for Support Tickets
 * IMPORTANT: Must end with /exec (not /dev)
 */
const TICKET_GOOGLE_SCRIPT_URL = process.env.REACT_APP_TICKET_WEBHOOK || 
  'https://script.google.com/macros/s/AKfycbws7nb4kiWwI7-axOogx943SnWJoMGpqpz1m7u3b0Y1ce7Av0p5L1ieHMunkkSie4bN/exec';

/**
 * Save support ticket data to Google Sheets (simplified columns)
 * @param {Object} ticketData - Complete ticket data from API response
 * @param {string} action - 'create' or 'update' (default: 'create')
 * @returns {Promise<Object>} Response
 */
export const saveTicketToSheet = async (ticketData, action = 'create') => {
  try {
    console.log(`📊 ${action === 'update' ? 'Updating' : 'Saving'} ticket data to Google Sheets...`);
    
    // Extract essential fields from ticket data
    const ticket = ticketData || {};
    const requester = ticket.requester || {};
    const sla = ticket.sla_info || {};
    const priority = ticket.priority || {};
    const severity = ticket.severity || {};
    const customFields = ticket.custom_fields || {};
    const counters = ticket.counters || {};
    const createdBy = ticket.created_by || {};
    
    // Get brand name from localStorage
    let brandName = '';
    try {
      const profileDataStr = localStorage.getItem('profileData');
      if (profileDataStr) {
        const profileData = JSON.parse(profileDataStr);
        brandName = profileData?.basic_details?.brand_name || '';
      }
    } catch (error) {
      console.warn('⚠️ Failed to get brand name from localStorage:', error);
    }
    
    // Build query string for GET request - Matching exact Google Sheet column headers
    // Brand Name is 2nd column (after Ticket Public ID)
    const params = new URLSearchParams({
      // Column order matches Google Sheet headers exactly
      'Ticket Public ID': ticket.public_id || '',
      'Brand Name': brandName, // 2nd column
      'Status': ticket.status || '',
      'Title': ticket.title || '',
      'Category': ticket.category || '',
      'Subcategory': ticket.subcategory || '',
      'Requester Actor ID': requester.actor_id || '',
      'Requester Name': requester.name || requester.cached_name || '',
      'Priority': priority.final_value || priority.suggested || '',
      'Severity': severity.final_value || severity.suggested || '',
      'Order Reference Number': customFields.order_reference_number || '',
      'AWB Number': customFields.awb_number || '',
      'Status Reason': ticket.status_reason || '',
      'Resolution': ticket.resolution || '',
      'Action': action, // 'create' or 'update'
      'SLA Breached': (sla.assignment_breached || sla.first_response_breached || sla.resolution_breached) ? 'Yes' : 'No',
      'Resolution Breached': sla.resolution_breached ? 'Yes' : 'No',
      'First Response Breached': sla.first_response_breached ? 'Yes' : 'No',
      'Created At': ticket.created_at || '',
      'Updated At': ticket.updated_at || '',
      'First Response At': ticket.first_response_at || '',
      'Message Count': counters.message_count || 0,
      'Reopen Count': counters.reopen_count || 0,
      'Assigned To': ticket.assignment?.name || ticket.assignment?.actor_id || '',
    });
    
    const fullUrl = `${TICKET_GOOGLE_SCRIPT_URL}?${params.toString()}`;
    console.log('📤 Sending to Google Sheets:', { action, ticketId: ticket.public_id, status: ticket.status });
    
    const response = await axios.get(fullUrl, {
      timeout: 15000, // 15 seconds
      headers: {
        'Accept': 'application/json',
      },
      validateStatus: function (status) {
        // Accept any status code (we'll handle it)
        return status >= 200 && status < 500;
      },
    });
    
    console.log('✅ Google Sheets response:', response.data);
    
    if (response.data?.success === false) {
      throw new Error(response.data?.error || `Failed to ${action} ticket in Google Sheets`);
    }
    
    return { success: true, data: response.data };
    
  } catch (error) {
    console.error(`❌ Failed to ${action} ticket in Google Sheets:`, error);
    console.error('❌ Error details:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      url: TICKET_GOOGLE_SCRIPT_URL
    });
    
    // Don't throw - this is non-critical, shouldn't stop ticket operations
    return { 
      success: false, 
      error: error.message || `Failed to ${action} ticket in Google Sheets` 
    };
  }
};

